import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SurveyCreatorComponent, SurveyCreator } from 'survey-creator-react';
import axios from 'axios';
import 'survey-core/defaultV2.min.css';
import 'survey-creator-core/survey-creator-core.min.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function SurveyBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showMetadata, setShowMetadata] = useState(!id);

  useEffect(() => {
    const creatorOptions = {
      showLogicTab: true,
      showTranslationTab: false,
      isAutoSave: false
    };

    const newCreator = new SurveyCreator(creatorOptions);
    
    if (!id) {
      newCreator.text = JSON.stringify({
        pages: [{
          name: 'page1',
          elements: [{
            type: 'text',
            name: 'question1',
            title: 'What is your feedback?'
          }]
        }]
      });
    }

    setCreator(newCreator);

    const interval = setInterval(() => {
      const input = document.querySelector('.sv-string-editor[aria-placeholder="Survey Title"]');
      if (input) {
        input.setAttribute('aria-placeholder', 'Form Title');
        clearInterval(interval);
      }
    }, 300);

    return () => clearInterval(interval);
  }, [id]);

  // Separate useEffect to load survey when creator is ready
  useEffect(() => {
    if (id && creator) {
      loadSurvey(id);
    }
  }, [id, creator]);

  const loadSurvey = async (surveyId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/surveys/${surveyId}`);
      const survey = response.data;
      
      setTitle(survey.title);
      setDescription(survey.description || '');
      
      // Set the survey JSON to the creator
      if (creator) {
        creator.text = JSON.stringify(survey.json);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load form: ' + err.message);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Please enter a form title');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const surveyJson = JSON.parse(creator.text);
      
      const surveyData = {
        title,
        description,
        json: surveyJson
      };

      if (id) {
        await axios.put(`${API_URL}/surveys/${id}`, surveyData);
        alert('Form updated successfully!');
      } else {
        const response = await axios.post(`${API_URL}/surveys`, surveyData);
        alert('Form created successfully!');
        navigate(`/builder/${response.data._id}`);
      }
      
      setLoading(false);
      setShowMetadata(false);
    } catch (err) {
      setError('Failed to save form: ' + err.message);
      setLoading(false);
    }
  };

  if (loading && !creator) {
    return <div className="loading">Loading form builder...</div>;
  }

  return (
    <div>
      <div className="container" style={{ marginBottom: '1rem' }}>
        <h1 className="page-title">{id ? 'Edit Form' : 'Create New Form'}</h1>
        
        {error && <div className="error">{error}</div>}

        <button 
          className="button button-secondary button-small"
          onClick={() => setShowMetadata(!showMetadata)}
          style={{ marginBottom: '1rem' }}
        >
          {showMetadata ? 'Hide' : 'Show'} Details
        </button>

        {showMetadata && (
          <div style={{ marginTop: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Form Title *</label>
              <input
                type="text"
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter form title"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter form description (optional)"
              />
            </div>
          </div>
        )}

        <div className="flex-end" style={{ marginTop: '1rem' }}>
          <button 
            className="button button-success" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : (id ? 'Update Form' : 'Save Form')}
          </button>
          
          <button 
            className="button button-secondary" 
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
        </div>
      </div>

      {creator && (
        <div style={{ height: '70vh', minHeight: '500px' }}>
          <SurveyCreatorComponent creator={creator} />
        </div>
      )}
    </div>
  );
}

export default SurveyBuilder;